document.addEventListener('DOMContentLoaded', () => {
    const container = document.querySelector('.minifig-builder-launcher');
    if (!container) return;

    const button = container.querySelector('.btn');
    const buttonText = button?.querySelector('.text');

    if (button) {
        button.style.opacity = '0';
        button.style.transition = 'opacity .4s ease';
    }

    const builderKeyRegex = /^minifigs-shopify-theme-(\d+\.\d+\.\d+)$/;

    const parseVersion = (v) => {
        return v.split('.').map((n) => parseInt(n, 10));
    };

    const compareVersions = (a, b) => {
        for (let i = 0; i < 3; i++) {
            if (a[i] > b[i]) return 1;
            if (a[i] < b[i]) return -1;
        }
        return 0;
    };

    const getLatestBuilderState = () => {
        const candidates = [];
        for (const key in localStorage) {
            const match = key.match(builderKeyRegex);
            if (match) {
                const version = match[1];
                candidates.push({ key, version, parts: parseVersion(version) });
            }
        }

        if (!candidates.length) return null;
        candidates.sort((a, b) => compareVersions(b.parts, a.parts));

        for (const c of candidates) {
            try {
                const raw = localStorage.getItem(c.key);
                if (!raw) continue;
                const parsed = JSON.parse(raw);
                return parsed || null;
            } catch (e) {
                // do nothing
            }
        }
        return null;
    };

    const updateButtonLabel = () => {
        if (!buttonText) return;
        const latest = getLatestBuilderState();
        const hasSkintone = !!(latest && Array.isArray(latest.builds) && latest.builds[0] && latest.builds[0].skintone);
        if (hasSkintone) {
            buttonText.textContent = 'Continue building';
        } else {
            if (!/start/i.test(buttonText.textContent)) {
                buttonText.textContent = 'Start building';
            }
        }
    };

    updateButtonLabel();

    if (button) {
        requestAnimationFrame(() => {
            button.style.opacity = '1';
        });
    }

    const cssHref = container.getAttribute('data-builder-css');
    const scriptSrc = container.getAttribute('data-builder-script');

    let loading = false;

    const ensureCss = () => {
        if (!cssHref) return;
        if (document.querySelector('link[data-minifig-builder-css]')) return;
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = cssHref;
        link.setAttribute('data-minifig-builder-css', 'true');
        document.head.appendChild(link);
    };

    let _lockedScrollY = 0;
    const enableScrollLock = () => {
        if (document.body.classList.contains('minifig-builder--active')) return;
        _lockedScrollY = window.scrollY || window.pageYOffset;
        const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
        if (scrollBarWidth > 0) {
            document.documentElement.style.setProperty('--builder-scrollbar-width', scrollBarWidth + 'px');
            document.body.style.paddingRight = scrollBarWidth + 'px';
        }
        document.body.style.top = `-${_lockedScrollY}px`;
        document.body.classList.add('minifig-builder--active');
        document.body.classList.add('minifig-builder-scroll-locked');
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
    };

    const disableScrollLock = () => {
        if (!document.body.classList.contains('minifig-builder-scroll-locked')) return;
        document.body.classList.remove('minifig-builder-scroll-locked');
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.top = '';
        document.body.style.paddingRight = '';
        window.scrollTo(0, _lockedScrollY);
    };

    // Focus trap state
    let previousActiveElement = null;
    let focusTrapListener = null;
    const FOCUSABLE_SELECTOR = [
        'a[href]',
        'area[href]',
        'button:not([disabled])',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'iframe',
        'object',
        'embed',
        '[contenteditable]',
        '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    const setupFocusTrap = (root) => {
        if (!root) return;
        // Skip trapping if page variant
        if (root.classList.contains('minifig-builder--page')) return;
        previousActiveElement = document.activeElement;
        // Ensure root has dialog semantics
        root.setAttribute('role', 'dialog');
        root.setAttribute('aria-modal', 'true');
        if (!root.getAttribute('aria-label') && !root.getAttribute('aria-labelledby')) {
            root.setAttribute('aria-label', 'Minifig builder');
        }
        const focusables = Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
            (el) => !el.hasAttribute('inert') && el.offsetParent !== null
        );
        const first = focusables[0];
        if (first) first.focus({ preventScroll: true });
        focusTrapListener = (e) => {
            if (e.key !== 'Tab') return;
            if (focusables.length === 0) {
                e.preventDefault();
                root.focus();
                return;
            }
            // Recompute in case of dynamic content
            const currentFocusables = Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
                (el) => !el.hasAttribute('inert') && el.offsetParent !== null
            );
            const firstEl = currentFocusables[0];
            const lastEl = currentFocusables[currentFocusables.length - 1];
            if (e.shiftKey) {
                if (document.activeElement === firstEl || document.activeElement === root) {
                    e.preventDefault();
                    lastEl?.focus();
                }
            } else {
                if (document.activeElement === lastEl) {
                    e.preventDefault();
                    firstEl?.focus();
                }
            }
        };
        document.addEventListener('keydown', focusTrapListener, true);
    };

    const teardownFocusTrap = () => {
        if (focusTrapListener) {
            document.removeEventListener('keydown', focusTrapListener, true);
            focusTrapListener = null;
        }
        if (previousActiveElement && typeof previousActiveElement.focus === 'function') {
            previousActiveElement.focus();
        } else if (button) {
            button.focus();
        }
        previousActiveElement = null;
    };

    const revealBuilder = () => {
        const article = document.querySelector('[data-minifig-builder-root="true"]');
        if (article) {
            enableScrollLock();
            article.style.display = '';
            article.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setupFocusTrap(article);
            // Attempt positioning of button container relative to #app once visible
            positionButtonContainerDeferred();
        }
    };

    const hideBuilder = () => {
        const article = document.querySelector('[data-minifig-builder-root="true"]');
        if (!article) return;
        article.style.display = 'none';
        document.body.classList.remove('minifig-builder--active');
        disableScrollLock();
        document.dispatchEvent(new CustomEvent('minifig-builder:closed'));
        teardownFocusTrap();
        if (button) button.focus();
        if (buttonText) {
            buttonText.textContent = 'Continue building';
        }
    };

    const loadBuilderScript = () => {
        if (!scriptSrc) return;
        if (document.querySelector('script[data-minifig-builder-script]')) {
            // Already present (maybe still loading)
            return;
        }
        const s = document.createElement('script');
        s.src = scriptSrc;
        s.defer = true;
        s.setAttribute('data-minifig-builder-script', 'true');
        s.onload = () => {
            loading = false;
            window.__MINIFIG_BUILDER_LOADED = true;
            document.dispatchEvent(new CustomEvent('minifig-builder:loaded'));
            // add body class + scroll lock once loaded
            enableScrollLock();
            revealBuilder();
        };
        document.head.appendChild(s);
    };

    const handleLaunch = (e) => {
        if (e) e.preventDefault();
        if (window.__MINIFIG_BUILDER_LOADED) {
            revealBuilder();
            return;
        }
        if (loading) return;
        loading = true;
        // Provide immediate user feedback while assets load
        if (button) {
            button.classList.add('is-loading');
            button.setAttribute('aria-busy', 'true');
            button.setAttribute('disabled', 'disabled');
        }
        if (buttonText) {
            if (!buttonText.dataset.originalText) {
                buttonText.dataset.originalText = buttonText.textContent || '';
            }
            buttonText.textContent = 'Opening builder...';
        }
        ensureCss();

        if (!document.querySelector('[data-minifig-builder-root="true"]')) {
            const article = document.createElement('article');
            article.className = 'minifig-builder';
            article.setAttribute('data-minifig-builder-root', 'true');
            article.style.display = 'none';
            article.innerHTML = "<h1>Create Your Own</h1><div id='app'></div>";
            document.body.appendChild(article);
        }
        loadBuilderScript();
    };

    if (button) {
        button.addEventListener('click', handleLaunch);
        const form = container.querySelector('.minifig-builder-launcher__form');
        if (form) {
            form.addEventListener('submit', handleLaunch);
        }
    }

    document.addEventListener('click', (e) => {
        const target = e.target;
        if (target && target.classList && target.classList.contains('minifig-builder__close')) {
            const article = target.closest('[data-minifig-builder-root="true"]');
            if (article && article.classList.contains('minifig-builder--page')) return;
            hideBuilder();
        }
    });

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && document.body.classList.contains('minifig-builder--active')) {
            const article = document.querySelector('[data-minifig-builder-root="true"]');
            if (article && article.classList.contains('minifig-builder--page')) return;
            hideBuilder();
        }
    });

    const positionButtonContainer = () => {
        const btnContainer = document.querySelector('.button-container');
        const app = document.querySelector('#app');
        if (!btnContainer || !app) return;
        const appRect = app.getBoundingClientRect();
        const btnRect = btnContainer.getBoundingClientRect();
        const desiredTop = Math.max(0, appRect.top - btnRect.height);
        const currentTop = parseFloat(btnContainer.style.top || '');
        if (isNaN(currentTop) || Math.abs(currentTop - desiredTop) > 1) {
            btnContainer.style.top = desiredTop + 'px';
        }
    };

    const positionButtonContainerDeferred = () => {
        let attempts = 0;
        const maxAttempts = 20;
        const interval = setInterval(() => {
            attempts++;
            positionButtonContainer();
            if (document.querySelector('.button-container')) {
                clearInterval(interval);
                window.addEventListener('resize', positionButtonContainer);
            }
            if (attempts >= maxAttempts) {
                clearInterval(interval);
            }
        }, 100);
    };

    document.addEventListener('minifig-builder:loaded', () => {
        positionButtonContainerDeferred();
        // Restore button state (in case user closes builder and returns)
        if (button) {
            button.classList.remove('is-loading');
            button.removeAttribute('aria-busy');
            button.removeAttribute('disabled');
        }
        if (buttonText) {
            // Re-evaluate label based on persisted state (may now have skintone)
            updateButtonLabel();
        }
    });
});
